import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaGoogle, FaUser, FaEnvelope, FaLock } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import SEO from './SEO';
import './Register.css';
import TermsOfService from './TermsOfService';

const Register = () => {
  const { signup, loginWithGoogle, error: authError, verificationSent } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    if (!termsAgreed) {
      return setError('You must agree to the Terms of Service to register');
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password, name);
      // Don't navigate - show verification message instead
    } catch (err) {
      setError(err.message || 'Failed to create an account');
    }
    setLoading(false);
  };

  const handleGoogleSignUp = async () => {
    try {
      setError('');
      setLoading(true);
      sessionStorage.setItem('authRedirect', '/');
      await loginWithGoogle();
    } catch (err) {
      setError('Failed to sign up with Google.');
      setLoading(false);
    }
  };

  const toggleTermsModal = () => {
    setShowTerms(!showTerms);
  };

  return (
    <>
      <SEO 
        title="Register - Student Helper"
        description="Create a new Student Helper account to access study materials and Q&A features."
        keywords="student helper register, sign up, create account, student registration"
      />
      <div className="register-container">
        <div className="wave-background"></div>
        <div className="circle-blob"></div>
        
        <motion.div 
          className="register-split-layout"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="register-left">
            {/* Left side empty for design aesthetic */}
          </div>
          
          <motion.div 
            className="register-form-section"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="register-form-container">
              <div className="register-header">
                <div className="register-logo">
                  <span>Welcome</span>
                </div>
                <h2>Register</h2>
                <p className="register-subtitle">Create your Student Helper account</p>
              </div>
              
              {verificationSent && (
                <motion.div 
                  className="alert alert-success"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>Verification email sent! Please check your inbox and verify your email address before logging in.</p>
                  <p>If you don't see the email, check your spam folder.</p>
                </motion.div>
              )}
              
              {error && (
                <motion.div 
                  className="alert alert-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}
              
              <form onSubmit={handleSubmit} className="register-form">
                <div className="form-group">
                  <FaUser className="input-icon" />
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="register-input"
                  />
                </div>
                <div className="form-group">
                  <FaEnvelope className="input-icon" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="register-input"
                  />
                </div>
                <div className="form-group">
                  <FaLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="register-input"
                  />
                </div>
                <div className="form-group">
                  <FaLock className="input-icon" />
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="register-input"
                  />
                </div>
                
                <div className="terms-checkbox-container">
                  <label className="terms-checkbox-label">
                    <input
                      type="checkbox"
                      checked={termsAgreed}
                      onChange={(e) => setTermsAgreed(e.target.checked)}
                      className="terms-checkbox"
                    />
                    <span>I agree to the <button 
                      type="button" 
                      className="terms-link"
                      onClick={toggleTermsModal}>
                      Terms of Service
                    </button></span>
                  </label>
                </div>
                
                <motion.button 
                  type="submit" 
                  disabled={loading} 
                  className="submit-button"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? "Creating Account..." : "Register"}
                </motion.button>
              </form>
              
              <div className="or-divider">
                <span>or</span>
              </div>
              
              <motion.button 
                onClick={handleGoogleSignUp} 
                disabled={loading} 
                className="google-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaGoogle className="google-icon" />
                Continue with Google
              </motion.button>
    
              <p className="login-link">
                Already have an account?
                <Link to="/login">
                  Sign In
                </Link>
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {showTerms && <TermsOfService onClose={toggleTermsModal} />}
    </>
  );
};

export default Register; 